import FullscreenOutlined from '@ant-design/icons/FullscreenOutlined'
import { Button, Modal, Spin, Typography } from 'antd'
import { ImageViewer } from 'features/files/components/ImageViewer'
import { PdfViewer } from 'features/files/components/PdfViewer'
import { useDocumentSignedUrls } from 'features/files/hooks/useDocuments'
import { RolodexDocumentEntry } from 'features/files/types'
import * as React from 'react'
import styled from 'styled-components'

const { Text } = Typography

export interface DocumentPreviewProps {
  document: RolodexDocumentEntry | null
  height?: number
  className?: string
  containerStyle?: React.CSSProperties
  continuousScroll?: boolean
  initialPage?: number
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const TitleContainer = styled.div`
  flex: 1;
`

const UnsupportedState = styled.div`
  text-align: center;
  padding: 1.25rem;
  color: #999;
`

const FullscreenPdfViewer = styled(PdfViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const FullscreenImageViewer = styled(ImageViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const ModalTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LoadingContainer = styled.div`
  text-align: center;
  padding: 1.25rem;
`

interface FullscreenModalProps {
  document: RolodexDocumentEntry
  signedUrl: string
  visible: boolean
  onClose: () => void
  isPdf: boolean
  isImage: boolean
  continuousScroll?: boolean
  initialPage?: number
}

const FullscreenModal: React.FC<FullscreenModalProps> = ({
  document,
  signedUrl,
  visible,
  onClose,
  isPdf,
  isImage,
  continuousScroll = false,
  initialPage,
}) => (
  <Modal
    open={visible}
    onCancel={onClose}
    footer={null}
    width='95vw'
    style={{ top: '1.25rem' }}
    styles={{
      body: {
        padding: 0,
        height: '90vh',
      },
    }}
    closable={true}
    title={
      <ModalTitleContainer>
        <span>{document.filename}</span>
      </ModalTitleContainer>
    }>
    {isPdf && (
      <FullscreenPdfViewer
        url={signedUrl}
        continuousScroll={continuousScroll}
        initialPage={initialPage}
      />
    )}
    {isImage && <FullscreenImageViewer url={signedUrl} height={1000} />}
  </Modal>
)

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  document,
  height = 400,
  className,
  containerStyle,
  continuousScroll = false,
  initialPage,
}) => {
  const { signedUrl, isLoading, isPdf, isImage } =
    useDocumentSignedUrls(document)
  const [isFullscreen, setIsFullscreen] = React.useState(false)

  if (!document) {
    return (
      <Container className={className} style={containerStyle}>
        <Text
          strong
          style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
          Document Preview
        </Text>
        <UnsupportedState>
          <Text type='secondary'>Select a document to preview</Text>
        </UnsupportedState>
      </Container>
    )
  }

  const canPreview = isPdf || isImage
  const previewTitle = isPdf
    ? 'PDF Preview'
    : isImage
      ? 'Image Preview'
      : 'File Preview'

  return (
    <>
      <Container className={className} style={containerStyle}>
        <HeaderContainer>
          <TitleContainer>
            <Text strong style={{ fontSize: 16, display: 'block' }}>
              {previewTitle}
            </Text>
          </TitleContainer>
          {canPreview && signedUrl && (
            <Button
              type='text'
              icon={<FullscreenOutlined />}
              onClick={() => setIsFullscreen(true)}
              title='Open in fullscreen'
            />
          )}
        </HeaderContainer>

        {isPdf ? (
          signedUrl ? (
            <PdfViewer
              url={signedUrl}
              height={height}
              continuousScroll={continuousScroll}
              showPagination={!continuousScroll}
              initialPage={initialPage}
            />
          ) : isLoading ? (
            <LoadingContainer>
              <Spin />
            </LoadingContainer>
          ) : (
            <UnsupportedState>
              <Text type='secondary'>Unable to load PDF preview</Text>
            </UnsupportedState>
          )
        ) : isImage ? (
          signedUrl ? (
            <ImageViewer url={signedUrl} height={height} />
          ) : isLoading ? (
            <LoadingContainer>
              <Spin tip='Loading image preview...' />
            </LoadingContainer>
          ) : (
            <UnsupportedState>
              <Text type='secondary'>Unable to load image preview</Text>
            </UnsupportedState>
          )
        ) : (
          <UnsupportedState>
            <Text type='secondary'>
              Preview not available for this file type
            </Text>
          </UnsupportedState>
        )}
      </Container>

      {document && signedUrl && canPreview && (
        <FullscreenModal
          document={document}
          signedUrl={signedUrl}
          visible={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          isPdf={Boolean(isPdf)}
          isImage={Boolean(isImage)}
          continuousScroll={continuousScroll}
          initialPage={initialPage}
        />
      )}
    </>
  )
}
